// Guess a MIME type from a filename extension. Some browsers hand back an EMPTY
// or generic `file.type` for common video containers (notably .mkv/.avi/.mov on
// certain platforms), which the S3 presign (routes/assets.ts) needs a
// non-empty type for. Callers use this as a fallback so the presign receives a
// real content-type. Pure function: no I/O, no globals — just extension → MIME.
//
// Unknown extensions return "" and the caller falls back to application/octet-stream;
// the server accepts any type for chat attachments and serves it as a download.
const EXT_TO_MIME: Record<string, string> = {
  // Video containers
  mkv: "video/x-matroska",
  avi: "video/x-msvideo",
  mp4: "video/mp4",
  m4v: "video/x-m4v",
  mov: "video/quicktime",
  webm: "video/webm",
  mpeg: "video/mpeg",
  mpg: "video/mpeg",
  ogv: "video/ogg",
  "3gp": "video/3gpp",
  wmv: "video/x-ms-wmv",
  flv: "video/x-flv",
  // Audio (agent and human uploads; m4a/opus normalised to the served types)
  mp3: "audio/mpeg",
  m4a: "audio/mp4",
  aac: "audio/aac",
  wav: "audio/wav",
  ogg: "audio/ogg",
  oga: "audio/ogg",
  opus: "audio/ogg",
  flac: "audio/flac",
  weba: "audio/webm",
  // Common images
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  webp: "image/webp",
  svg: "image/svg+xml",
  // Documents (download-only on the server; some browsers hand back an empty
  // file.type for these, so the presign needs a guessed mime to avoid a 415).
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  csv: "text/csv",
  rtf: "application/rtf",
  odt: "application/vnd.oasis.opendocument.text",
  odp: "application/vnd.oasis.opendocument.presentation",
  ods: "application/vnd.oasis.opendocument.spreadsheet",
};

export function guessContentType(filename: string): string {
  const dot = filename.lastIndexOf(".");
  if (dot < 0 || dot === filename.length - 1) return "";
  const ext = filename.slice(dot + 1).toLowerCase();
  return EXT_TO_MIME[ext] ?? "";
}

// Mirrors the server's media allowlist (routes/assets.ts); anything else is a download.
// Pinned to the server sets by routes/assets-allowlist-parity.test.ts.
export const INLINE_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/gif", "image/webp"]);
const SERVED_AV_TYPES = new Set([
  "audio/webm",
  "audio/ogg",
  "audio/mpeg",
  "audio/mp3",
  "audio/mp4",
  "audio/aac",
  "audio/wav",
  "audio/flac",
  "audio/x-aiff",
  "audio/aiff",
  "video/mp4",
  "video/webm",
  "video/quicktime",
  "video/x-matroska",
  "video/x-msvideo",
  "video/mpeg",
  "video/x-m4v",
  "video/ogg",
  "video/3gpp",
  "video/x-ms-wmv",
  "video/x-flv",
]);

export function isInlineImageType(type: string): boolean {
  return INLINE_IMAGE_TYPES.has(type);
}

// Aliases a browser or sniffer may report; the relay normalises them the same way.
const AUDIO_ALIASES: Record<string, string> = { "audio/x-m4a": "audio/mp4" };

export function registeredAttachmentType(type: string): string {
  type = AUDIO_ALIASES[type] ?? type;
  if (type.startsWith("image/")) return isInlineImageType(type) ? type : "application/octet-stream";
  if (type.startsWith("audio/") || type.startsWith("video/")) {
    return SERVED_AV_TYPES.has(type) ? type : "application/octet-stream";
  }
  return type;
}

export type AttachmentKind = "image" | "video" | "audio" | "file";

// One classifier for every draft surface (the web strip and the native composer).
export function attachmentKind(type: string): AttachmentKind {
  const registered = registeredAttachmentType(type);
  if (isInlineImageType(registered)) return "image";
  if (registered.startsWith("video/")) return "video";
  if (registered.startsWith("audio/")) return "audio";
  return "file";
}
